import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H3,
  H6,
  Icon,
  IconType,
} from "@sellar-io/components";
import {
  SupplierRelationshipGetResponseType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsType,
} from "@sellar-io/api-v2/types";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { StorefrontPreviewer } from "../../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { ReactNode, useState } from "react";
import { usePaymentTermsIndicator } from "../../../components/paymentTypeIndicator/usePaymentTermsIndicator";
import { AssignProductCatalogueDialog } from "./AssignProductCatalogueDialog";
import { AssignPriceListDialog } from "./AssignPriceListDialog";
import { useSupplierRelationshipUpdate } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipUpdate";
import { AssignPaymentTermsDialog } from "./AssignPaymentTermsDialog";
import { EditVatDialog } from "./EditVatDialog";
import { useNavigate } from "react-router";
import { CUSTOMER_ZONE } from "../../../constants/routes";
import { AssignDeliveryDialog } from "./AssignDeliveryDialog";

export const BusinessConnectionTradeTerms = ({
  connection,
}: {
  connection: SupplierRelationshipGetResponseType;
}) => {
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );

  const navigate = useNavigate();
  const customerZoneId = connection.Relationship?.CustomerZone?.id;

  const [assignPaymentTermsDialogActive, setAssignPaymentTermsDialogActive] =
    useState(false);

  const [assignPriceListDialogActive, setAssignPriceListDialogActive] =
    useState(false);

  const [
    assignProductCatalogueDialogActive,
    setAssignProductCatalogueDialogActive,
  ] = useState(false);

  const [assignDeliveryDialogActive, setAssignDeliveryDialogActive] =
    useState(false);

  const [editVatDialogActive, setEditVatDialogActive] = useState(false);

  const drayDeliveryMethod =
    connection.DrayDeliveryMethod ??
    connection.Relationship?.CustomerZone?.DrayDeliveryMethod ??
    null;
  const courierDeliveryMethod =
    connection.CourierDeliveryMethod ??
    connection.Relationship?.CustomerZone?.CourierDeliveryMethod ??
    null;
  const palletDeliveryMethod =
    connection.PalletDeliveryMethod ??
    connection.Relationship?.CustomerZone?.PalletDeliveryMethod ??
    null;

  const deliveryMethods = [
    drayDeliveryMethod ? (drayDeliveryMethod.name ?? "Direct") : null,
    courierDeliveryMethod
      ? (courierDeliveryMethod.name ?? "Courier delivery")
      : null,

    palletDeliveryMethod
      ? (palletDeliveryMethod.name ?? "Pallet delivery")
      : null,
  ]
    .filter(Boolean)
    .join(", ");

  const priceTier =
    connection.PriceTier ??
    connection.Relationship?.CustomerZone?.PriceTier ??
    null;

  const availabilityTier =
    connection.AvailabilityTier ??
    connection.Relationship?.CustomerZone?.AvailabilityTier ??
    null;

  const paymentTerms = usePaymentTermsIndicator({
    connection: {
      ...connection,
      paymentDueCalculateFrom:
        connection.paymentDueCalculateFrom as SupplierRelationshipPaymentDueCalculateFromType,
      paymentTerms:
        connection.paymentTerms as SupplierRelationshipPaymentTermsType,
    },
    invoiceCreationStage: connection.Supplier.invoiceCreationStage,
  });

  const updateConnectionMutation = useSupplierRelationshipUpdate(connection.id);

  const loading = updateConnectionMutation.isPending;

  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge(
            "bg-white",
            "flex items-center justify-between gap-2",
          )}
        >
          <div>
            <H3>Trade terms</H3>
            <H6>
              Your customers will automatically inherit the terms set on their
              Customer Zone, unless you directly assign them different terms.
            </H6>
          </div>
          {isSupplierStorefrontFeatureFlagEnabled ? (
            <div className="order-3">
              <StorefrontPreviewButton customer={connection} />
            </div>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col divide-y overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <TradeTermRow
              icon="MAP_MARKER"
              title="Customer zone"
              info={connection.Relationship?.CustomerZone?.name ?? "N/A"}
              action={
                customerZoneId === undefined ? null : (
                  <Button
                    color="transparent"
                    size="small"
                    className="flex items-center gap-1"
                    onClick={() =>
                      navigate(
                        CUSTOMER_ZONE.href({
                          customerZoneId,
                        }),
                      )
                    }
                  >
                    View <Icon icon="CHEVRON_RIGHT" size={0.7} />
                  </Button>
                )
              }
            />
          </div>
          <div className="flex flex-col divide-y overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <TradeTermRow
              icon="TAG_OUTLINE"
              title="Pricing"
              info={
                connection.PriceTierId ? (
                  <DirectAssignmentText text={priceTier?.name ?? "Pricing"} />
                ) : (
                  (priceTier?.name ?? "Pricing")
                )
              }
              action={
                <Button
                  color="transparent"
                  size="small"
                  className="flex items-center gap-1"
                  onClick={() => setAssignPriceListDialogActive(true)}
                >
                  Edit <Icon icon="PENCIL" size={0.7} />
                </Button>
              }
            />
            <TradeTermRow
              icon="BOOK_OUTLINE"
              title="Product catalogue"
              info={
                connection.AvailabilityTierId ? (
                  <DirectAssignmentText
                    text={availabilityTier?.name ?? "Product catalogue"}
                  />
                ) : (
                  (availabilityTier?.name ?? "Product catalogue")
                )
              }
              action={
                <Button
                  color="transparent"
                  size="small"
                  className="flex items-center gap-1"
                  onClick={() => setAssignProductCatalogueDialogActive(true)}
                >
                  Edit <Icon icon="PENCIL" size={0.7} />
                </Button>
              }
            />
            <TradeTermRow
              icon="TRUCK_OUTLINE"
              title="Delivery"
              info={deliveryMethods}
              action={
                <Button
                  color="transparent"
                  size="small"
                  className="flex items-center gap-1"
                  onClick={() => setAssignDeliveryDialogActive(true)}
                >
                  Edit <Icon icon="PENCIL" size={0.7} />
                </Button>
              }
            />
          </div>
          <div className="flex flex-col divide-y overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <TradeTermRow
              icon="HANDSHAKE"
              title="Payment terms"
              info={paymentTerms.fullTextOnly || "N/A"}
              action={
                <Button
                  color="transparent"
                  size="small"
                  className="flex items-center gap-1"
                  onClick={() => setAssignPaymentTermsDialogActive(true)}
                >
                  Edit <Icon icon="PENCIL" size={0.7} />
                </Button>
              }
            />
            <TradeTermRow
              icon="PERCENT"
              title="VAT percentage"
              info={
                connection.vatPercentage ? `${connection.vatPercentage}%` : "0%"
              }
              action={
                <Button
                  color="transparent"
                  size="small"
                  className="flex items-center gap-1"
                  onClick={() => setEditVatDialogActive(true)}
                >
                  Edit <Icon icon="PENCIL" size={0.7} />
                </Button>
              }
            />
          </div>
        </CardContent>
      </Card>
      <AssignPaymentTermsDialog
        active={assignPaymentTermsDialogActive}
        setActive={setAssignPaymentTermsDialogActive}
        connection={connection}
      />
      <AssignPriceListDialog
        active={assignPriceListDialogActive}
        setActive={setAssignPriceListDialogActive}
        priceListId={connection.PriceTierId}
        loading={loading}
        onClick={(priceListId) => {
          updateConnectionMutation.mutate(
            {
              PriceTierId: priceListId,
              AvailabilityTierId: connection.AvailabilityTierId ?? null,
            },
            {
              onSuccess: () => {
                setAssignPriceListDialogActive(false);
              },
            },
          );
        }}
      />
      <AssignProductCatalogueDialog
        active={assignProductCatalogueDialogActive}
        setActive={setAssignProductCatalogueDialogActive}
        availabilityTierId={connection.AvailabilityTierId}
        loading={loading}
        onClick={(availabilityTierId) => {
          updateConnectionMutation.mutate(
            {
              PriceTierId: connection.PriceTierId ?? null,
              AvailabilityTierId: availabilityTierId,
            },
            {
              onSuccess: () => {
                setAssignProductCatalogueDialogActive(false);
              },
            },
          );
        }}
      />
      <AssignDeliveryDialog
        active={assignDeliveryDialogActive}
        setActive={setAssignDeliveryDialogActive}
        deliveryIds={connection}
        loading={loading}
        onClick={(deliveryMethodIds) => {
          updateConnectionMutation.mutate(deliveryMethodIds, {
            onSuccess: () => {
              setAssignDeliveryDialogActive(false);
            },
          });
        }}
      />
      <EditVatDialog
        active={editVatDialogActive}
        setActive={setEditVatDialogActive}
        connection={connection}
      />
    </>
  );
};

const TradeTermRow = ({
  icon,
  title,
  info,
  action,
}: {
  icon: IconType;
  title: ReactNode;
  info: ReactNode;
  action: ReactNode;
}) => {
  const isDesktop = useMediaQuery("(min-width: 700px)");

  return (
    <div className="flex w-full items-center">
      <div
        className={twMerge(
          "flex min-w-0 flex-1 px-4 py-3 text-sm font-medium",
          isDesktop
            ? "w-48 flex-none border-r border-zinc-200 bg-zinc-50"
            : "flex-col gap-1",
        )}
      >
        <div className="flex items-center gap-2">
          <Icon icon={icon} className="flex-none" size={0.7} />
          {title}
          {!isDesktop && (
            <span className="overflow-hidden text-xs font-normal text-nowrap text-ellipsis">
              {info}
            </span>
          )}
        </div>
      </div>
      {isDesktop && (
        <div className="overflow-hidden px-4 text-ellipsis">
          <p className="overflow-hidden text-sm text-nowrap text-ellipsis">
            {info}
          </p>
        </div>
      )}
      <div className="mr-2 ml-auto flex flex-none items-center gap-2">
        {action}
      </div>
    </div>
  );
};

const DirectAssignmentText = ({ text }: { text: string }) => {
  return (
    <>
      {text} <span className="text-blue-700">(directly assigned)</span>
    </>
  );
};

const StorefrontPreviewButton = ({
  customer,
}: {
  customer: SupplierRelationshipGetResponseType;
}) => {
  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    productListQuery,
    tab,
    selectedCustomerZone,
    onChangeTab,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    prefillCustomer,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <DataButton
          border={true}
          onClick={() => {
            prefillCustomer({
              customer,
            });
            setOpenStorefrontPreviewer(true);
          }}
        >
          <Icon icon="EYE" />
          Preview
        </DataButton>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer: selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};
